import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next';
import StaffService from '../../services/staff.service';
import InteractiveMap from '../common/map/InteractiveMap';
import SiteService from '../../services/site.service';
import Expandable from '../common/Expandable';
import MapControl from './MapControl';
import JobTable from '../jobs/JobTable';
import SitesTable from '../sites/site/SiteTable';
import L from 'leaflet';
import { UserType } from '../../types/Staff';

var technicianIcon = new L.Icon({
  iconUrl: require("../../img/worker.png"),
  iconSize: [25, 25], 
});

var siteIcon = new L.Icon({
  iconUrl: require("../../img/pin.png"),
  iconSize: [25, 25], 
});

export default function MapPage() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [staffCoords, setStaffCoords] = useState<[number, number][]>([]);
  const [siteCoords, setSiteCoords] = useState<[number, number][]>([]);
  const [size, setSize] = useState({ width: '45%', height: '100%' });
  const [isTechniciansEnabled, setIsTechniciansEnabled] = useState(true);
  const [isSitesEnabled, setIsSitesEnabled] = useState(true);
  const [isTechniciansTableEnabled, setIsTechniciansTableEnabled] = useState(false);
  const [isSitesTableEnabled, setIsSitesTableEnabled] = useState(false);
  const resizableRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();

  const toggleContent = () => {
    if (!isExpanded) {
      setIsExpanded(!isExpanded);
    }
  };

  const openTechnicianTable = (toggleOn: boolean) => {
    toggleContent();

    setIsTechniciansTableEnabled(true);
    setIsSitesTableEnabled(false);
  }

  const openSitesTable = (toggleOn: boolean) => {
    toggleContent();

    setIsSitesTableEnabled(true);
    setIsTechniciansTableEnabled(false);
  }

  const closeContent = () => {
    setIsExpanded(false);
  }

  const handleResize = (newWidth: string) => {
    setSize(prevSize => ({ ...prevSize, width: newWidth }));
  };

  const toggleTechniciansVisibility = (toggleOn: boolean) => {
    setIsTechniciansEnabled(toggleOn);
  };

  const toggleSitesVisibility = (toggleOn: boolean) => {
    setIsSitesEnabled(toggleOn);
  };

  const fetchData = async () => {
    const staffData = await StaffService.getStaff(); 
    const siteData = await SiteService.getSites(); 
    
    const staffJson = await staffData.json();
    const siteJson = await siteData.json();

    // @ts-ignore
    const staffCoords = staffJson.filter(s => s.userType === UserType.STAFF && s.location).map(s => [s.location.coordinates[1], s.location.coordinates[0]]);
    // @ts-ignore
    const siteCoords = siteJson.filter(s => s.location).map(s => [s.location?.coordinates[1], s.location?.coordinates[0]]);
  
    setStaffCoords(staffCoords); 
    setSiteCoords(siteCoords);  
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div
      className="w-screen relative flex"
      style={{ height: `calc(100vh - 75px)` }}
    >
      <InteractiveMap
        coordinates={[-97.5164, 35.4676]}
        className="relative rounded-md shadow-md z-0 h-full w-full flex-grow"
        markers={[
          {
            icon: technicianIcon,
            entities: isTechniciansEnabled ? staffCoords: []
          },
          {
            icon: siteIcon,
            entities: isSitesEnabled ? siteCoords: []
          }
        ]}
      />
      <div
        ref={resizableRef}
        className="absolute left-4 top-4 transform w-fit bg-white p-4 shadow-lg z-50 rounded-md"
      >
        <div className="flex flex-col w-full">
          <div className="flex items-center gap-2 w-full">
            <h2 className="text-lg font-bold">{t('map.filter')}</h2>
          </div>

          <div className="mt-2 flex flex-col gap-4 w-full">
            <MapControl
              label={t('map.technician')}
              toggleVisibility={toggleTechniciansVisibility}
              toggleContent={openTechnicianTable}
            />

            <MapControl
              label={t('map.sites')}
              toggleVisibility={toggleSitesVisibility}
              toggleContent={openSitesTable}
            />
          </div>
        </div>
      </div>

      <Expandable
        isExpanded={isExpanded}
        size={size}
        onResize={handleResize}
        title={
          isTechniciansTableEnabled
            ? t('map.technician')
            : isSitesTableEnabled
            ? t('map.sites')
            : null
        }
        children={
          isTechniciansTableEnabled ? (
            <JobTable />
          ) : isSitesTableEnabled ? (
            <SitesTable />
          ) : null
        }
        closeContent={closeContent}
      />
    </div>
  );
}
